import { View, SetReminderModal, EditMessageModal } from '@/components'
import { theme } from '@/config'
import { Chat } from '@/config/packages'
import { alert } from '@codeleap/modals'
import {
  isChatGroup,
  isMessageDeleted,
  isThreadParentMessage,
  useMessageGrouping,
  isMessageEdited,
  isMessageForwarded,
  ChatFileTypes,
  isPollMessage,
} from '@packages/chat'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Channel } from 'stream-chat'
import { useChannelStateContext, useChatContext, useMessageContext } from 'stream-chat-react'
import {
  MessageCheckbox,
  MessageFooter,
  MessageTail,
  EmojiReactionButton,
} from './components'
import { MessageAvatar } from './components/MessageAvatar'
import { MessageBubble } from './components/MessageBubble'
import { useChatMessageStyles } from './styles'
import { MessageProps, MessageStatusTypes } from './types'
import { useDoubleClick } from '@/hooks/useDoubleClick'

export type ChatMessageProps = MessageProps & {
  channel: Channel
  firstGroupMessage?: boolean
}

const MessageAvatarWrapper = memo(({ isMyMessage, noFooter, threadList }: { isMyMessage: boolean; noFooter: boolean } & Pick<MessageProps, 'threadList'>) => {
  const selectMode = Chat.Store.useSelectMode({ threadList }).selectModeActive

  if (isMyMessage || selectMode) return null

  return (
    <>
      {!noFooter ? <MessageAvatar /> : <View style={{ width: theme.size.largeish, height: theme.size.largeish }} />}
    </>
  )
}, (prevProps, nextProps) => {
  return prevProps.isMyMessage === nextProps.isMyMessage && prevProps.noFooter === nextProps.noFooter
})

export const ChatMessageWithContext = (props: ChatMessageProps) => {
  const { message, lastGroupMessage, firstGroupMessage, channel, threadList, handleOpenThread } = props

  const isMyMessage = props.isMyMessage()
  const { client } = useChatContext()
  const { selectModeActive } = Chat.Store.useSelectMode({ threadList })
  const isMessageSelected = Chat.Store.useIsMessageSelected(message.id)
  const isThreadParent = isThreadParentMessage(message, threadList)
  const pollId = message?.poll_id
  const messageId = message?.id

  const isDeleted = isMessageDeleted(message)
  const isEdited = isMessageEdited(message)
  const isForwarded = isMessageForwarded(message)

  const [editModalVisible, setEditModalVisible] = useState(false)
  const toggleEditModal = useCallback(() => setEditModalVisible(prev => !prev), [])

  // Memoizar cálculos derivados para evitar recálculos a cada render
  const { isPoll, noFooter, hasImages, hasAudioAttachment } = useMemo(() => ({
    isPoll: !!pollId,
    noFooter: lastGroupMessage === false && message?.status === MessageStatusTypes.RECEIVED,
    hasImages: message?.attachments?.some(
      attachment => attachment.type === ChatFileTypes.Image && !!attachment.image_url && !attachment?.title_link,
    ) || false,
    hasAudioAttachment: message?.attachments?.some(
      attachment => attachment.type === ChatFileTypes.Audio || attachment.type === ChatFileTypes.VoiceRecording,
    ) || false,
  }), [pollId, lastGroupMessage, message?.status, message?.attachments])

  useEffect(() => {
    if (!isPoll || !pollId || !client?.polls?.fromState) return

    const existingPoll = client.polls.fromState(pollId)

    if (!existingPoll && messageId) {
      try {
        client.polls.hydratePollCache([message], true)
      } catch (e) {
        console.warn('Failed to hydrate poll cache for message', messageId, e)
      }
    }
  }, [client, isPoll, pollId, messageId])

  const hasTail = (!isThreadParent || hasAudioAttachment) && !noFooter

  const { styles, tailProps } = useChatMessageStyles({
    isDeleted,
    noFooter,
    isMyMessage,
    isThreadParent,
    hasAttachments: message?.attachments?.length > 0,
    hasImages,
    hasAudioAttachment,
    hasTail: hasTail,
  })

  const userName = message?.user?.name || 'Unknown User'
  const isGroup = isChatGroup(channel)

  const handleDelete = useCallback(() => {
    Chat.Store.setSelectMode('delete', [message])
  }, [message])

  const handleSelect = useCallback(() => {
    const selectMode = Chat.Store.getSelectMode({ threadList })

    if (selectMode === 'delete' && !isMyMessage) {
      return
    }

    Chat.Store.selectMessage(message)
  }, [message, isMyMessage])

  const handleForward = useCallback(() => {
    Chat.Store.setSelectMode('forward', [message])
  }, [message])

  const handleReply = useCallback((e) => {
    handleOpenThread?.(e)
  }, [handleOpenThread])

  const handleCopy = useCallback(async () => {
    if (message?.text) {
      try {
        await navigator.clipboard.writeText(message.text)
        Chat.Store.setToastMessage('Message copied')
      } catch (error) {
        alert.error({
          title: 'Error',
          body: 'Failed to copy message to clipboard',
        })
      }
    }
  }, [message?.text])

  const handleStar = useCallback(() => {
    alert.info({
      title: 'Star Message',
      body: 'Star functionality will be implemented',
    })
  }, [])

  const handleSetReminder = useCallback(() => {
    SetReminderModal.open({ message })
  }, [])

  const handleEdit = useCallback(() => {
    setEditModalVisible(true)
  }, [])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDoubleTap = useCallback(async () => {
    if (isMessageDeleted(message)) {
      return
    }

    try {
      await Chat.Services.handleReaction({
        channel,
        emoji: '👍',
        message,
      })
    } catch (error) {
    }
  }, [channel, message])

  const handleClick = useCallback((e: React.MouseEvent) => {
    const selectMode = Chat.Store.getSelectMode({ threadList })
    if (selectMode) {
      e.stopPropagation()

      if (selectMode === 'delete' && !isMyMessage) {
        return
      }

      if (isPollMessage(message)) {
        if (selectMode === 'forward') {
          Chat.Store.setToastMessage('Polls cannot be forwarded')
          return
        }
      }

      Chat.Store.selectMessage(message)
    }
  }, [threadList, message, isMyMessage])

  const { onDoubleClick, cleanup } = useDoubleClick(handleDoubleTap, handleClick)

  useEffect(() => {
    return cleanup
  }, [cleanup])

  const handleRowClick = useCallback((e: React.MouseEvent) => {
    const modal = (e.target as Element)?.closest('.str-chat__modal')
    if (modal) {
      e.stopPropagation()
      return
    }

    if (selectModeActive) {
      handleClick(e)
    } else {
      onDoubleClick(e)
    }
  }, [selectModeActive, handleClick, onDoubleClick])

  return (
    <View
      css={[styles.wrapper]}
      className='chat-message-row'
      onClick={handleRowClick}
      onContextMenu={handleContextMenu}
      style={selectModeActive && { cursor: 'pointer' }}
      data-message-id={message.id}
    >
      <div css={[styles.messageContainer]}>
        <MessageCheckbox onClick={handleSelect} isSelected={isMessageSelected} {...props} />

        {(!isThreadParent || hasAudioAttachment) && (
          <MessageAvatarWrapper isMyMessage={isMyMessage} noFooter={noFooter} threadList={threadList} />
        )}

        <div css={[styles.messageInnerContainer]}>
          {hasTail && (
            <MessageTail {...tailProps} />
          )}

          <MessageBubble
            message={message}
            channel={channel}
            isMyMessage={isMyMessage}
            isDeleted={isDeleted}
            isEdited={isEdited}
            isForwarded={isForwarded}
            isThreadParent={isThreadParent}
            hasAudioAttachment={hasAudioAttachment}
            isGroup={isGroup}
            firstGroupMessage={firstGroupMessage}
            userName={userName}
            isPoll={isPoll}
            threadList={threadList}
            styles={styles}
            hasTail={hasTail}
            onDelete={handleDelete}
            onSelect={handleSelect}
            onReply={handleReply}
            onCopy={handleCopy}
            onStar={handleStar}
            onForward={handleForward}
            onSetReminder={handleSetReminder}
            onEdit={handleEdit}
          />

          {!isThreadParent && <EmojiReactionButton threadList={threadList} />}
        </div>
      </div>

      {(!noFooter || isThreadParent) && <MessageFooter style={styles.footer} />}

      {/* <SetReminderModal
        visible={reminderModalVisible}
        toggle={() => setReminderModalVisible(!reminderModalVisible)}
        message={message}
      /> */}

      {editModalVisible && (
        <EditMessageModal
          visible={editModalVisible}
          toggle={toggleEditModal}
          message={message}
          channel={channel}
        />
      )}
    </View>
  )
}

const areEqual = (
  prevProps: MessageProps,
  nextProps: MessageProps,
) => {
  const { message: prevMessage, readBy: prevReadBy } = prevProps
  const { message: nextMessage, readBy: nextReadBy } = nextProps

  // Comparacao por referencia (muito mais rapido que JSON.stringify)
  if (prevReadBy !== nextReadBy) return false

  // Comparar grouping
  if (prevProps?.lastGroupMessage !== nextProps?.lastGroupMessage) return false
  if (prevProps?.firstGroupMessage !== nextProps?.firstGroupMessage) return false

  // Comparar campos primitivos diretamente
  if (prevMessage?.reply_count !== nextMessage?.reply_count) return false
  if (prevMessage?.deleted_at !== nextMessage?.deleted_at) return false
  if (prevMessage?.pinned !== nextMessage?.pinned) return false
  if (prevMessage?.message_text_updated_at !== nextMessage?.message_text_updated_at) return false
  if (prevMessage?.text !== nextMessage?.text) return false
  if (prevMessage?.status !== nextMessage?.status) return false
  if (prevMessage?.type !== nextMessage?.type) return false

  // Comparar reactions por referencia (Stream Chat atualiza a referencia quando muda)
  if (prevMessage?.latest_reactions !== nextMessage?.latest_reactions) return false
  if (prevMessage?.reaction_counts !== nextMessage?.reaction_counts) return false
  if (prevMessage?.own_reactions !== nextMessage?.own_reactions) return false

  const latestReactionsEqual =
    Array.isArray(prevMessage.latest_reactions) && Array.isArray(nextMessage.latest_reactions)
      ? prevMessage.latest_reactions.length === nextMessage.latest_reactions.length &&
      prevMessage.latest_reactions.every(
        ({ type }, index) => type === nextMessage.latest_reactions?.[index].type,
      )
      : prevMessage.latest_reactions === nextMessage.latest_reactions
  if (!latestReactionsEqual) {
    return false
  }

  return true
}

const MemoizedChatMessage = memo(ChatMessageWithContext, areEqual)

export const ChatMessage = () => {
  const props = useMessageContext()
  const { channel } = useChannelStateContext()
  // const { openThread } = Getstream.Chat.useThreadNavigation()

  const grouping = useMessageGrouping({ message: props.message, groupStyles: props.groupStyles })

  return <MemoizedChatMessage {...props} {...grouping} channel={channel} />
}
